/**
 * Assets feature module (W2-10 projects-data; DESIGN.md §4.18; WORKPACKAGES Appendix B: `assets.*`).
 *
 * Provides:
 *  - the Assets dock tab (`chart.dock`, panel id 'assets');
 *  - commands: `assets.show`, `assets.add` (`{ role? }`), `assets.replace` (`{ asset }`, the checker's "choose
 *    file" fix for missing or undecodable files), `assets.remove`, `assets.rename`, `assets.downscale`,
 *    `assets.download` (each `{ asset }`), `assets.removeUnused`;
 *  - a help topic about the chart's files and Phira's naming rules;
 *  - `init`: playback follows the chart's song when an edit, undo, redo or version restore changes it
 *    (songSync.ts), and the files of an opened chart are checked for bytes the browser lost (presence.ts).
 */

import type { CommandContext, CommandDef, FeatureModule, HelpTopicDef } from '../../app/registry/types';
import type { AssetId, AssetRole } from '../../core/model/types';
import type { MessageRef } from '../../core/util/types';
import { m } from './assets.messages';
import {
  downloadAsset,
  downscaleAsset,
  pickAndAdd,
  removeAsset,
  removeUnused,
  renameAsset,
  replaceAsset,
} from './assetActions';
import { ROLE_ORDER } from './assetModel';
import { AssetsPanel } from './AssetsPanel';
import { assetsUi } from './assetsUi';
import { watchAssetPresence } from './presence';
import { followSong } from './songSync';

function projectOpen(ctx: CommandContext): true | MessageRef {
  return ctx.store !== null ? true : { key: m.reasonNoProject };
}

function obj(args: unknown): Readonly<Record<string, unknown>> {
  return typeof args === 'object' && args !== null && !Array.isArray(args) ? (args as Record<string, unknown>) : {};
}

function roleArg(args: unknown): AssetRole | null {
  const v = typeof args === 'string' ? args : obj(args).role;
  return typeof v === 'string' && (ROLE_ORDER as readonly string[]).includes(v) ? (v as AssetRole) : null;
}

/** The file a command acts on (`{ asset }`); without one the panel opens and says to pick a file. */
function assetArg(ctx: CommandContext, args: unknown): AssetId | null {
  const v = typeof args === 'string' ? args : obj(args).asset;
  if (typeof v === 'string' && v !== '') {
    if (ctx.store?.doc.assets.has(v as AssetId) === true) return v as AssetId;
    ctx.ui.toast({ key: m.gone }, { kind: 'info' });
    return null;
  }
  ctx.ui.showPanel('assets');
  ctx.ui.toast({ key: m.pickAsset }, { kind: 'info' });
  return null;
}

export const ASSETS_COMMANDS: readonly CommandDef[] = [
  {
    id: 'assets.show',
    title: m.cmdShow,
    category: 'File',
    icon: 'Files',
    level: 'guided',
    aliases: ['assets', 'files', 'resources', 'textures', 'hitsounds', '资源', '文件'],
    menus: [{ menu: 'app.project', group: 'project', order: 20 }],
    enabled: projectOpen,
    run(ctx) {
      ctx.ui.showPanel('assets');
    },
  },
  {
    id: 'assets.add',
    title: m.cmdAdd,
    category: 'File',
    icon: 'Upload',
    level: 'guided',
    aliases: ['add file', 'import file', 'add texture', 'add hitsound', 'add image', '添加文件'],
    enabled: projectOpen,
    async run(ctx, args) {
      await pickAndAdd(ctx, roleArg(args));
    },
  },
  {
    id: 'assets.replace',
    title: m.cmdReplace,
    category: 'File',
    icon: 'Replace',
    level: 'guided',
    aliases: ['replace file', 'choose file', 'missing file', 'change song', '替换文件'],
    enabled: projectOpen,
    async run(ctx, args) {
      const id = assetArg(ctx, args);
      if (id !== null) await replaceAsset(ctx, id);
    },
  },
  {
    id: 'assets.remove',
    title: m.cmdRemove,
    category: 'File',
    icon: 'Trash2',
    level: 'guided',
    aliases: ['remove file', 'delete file', '删除文件'],
    enabled: projectOpen,
    async run(ctx, args) {
      const id = assetArg(ctx, args);
      if (id !== null) await removeAsset(ctx, id);
    },
  },
  {
    id: 'assets.rename',
    title: m.cmdRename,
    category: 'File',
    icon: 'Pencil',
    level: 'guided',
    aliases: ['rename file', 'file name', '重命名文件'],
    enabled: projectOpen,
    run(ctx, args) {
      const id = assetArg(ctx, args);
      if (id === null) return;
      const path = obj(args).path;
      if (typeof path === 'string') {
        renameAsset(ctx, id, path);
        return;
      }
      ctx.ui.showPanel('assets');
      assetsUi.setRenaming(id);
    },
  },
  {
    id: 'assets.downscale',
    title: m.cmdDownscale,
    category: 'File',
    icon: 'ImageDown',
    level: 'guided',
    aliases: ['downscale', 'resize image', 'shrink image', 'mipmaps', '缩小图片'],
    enabled: projectOpen,
    async run(ctx, args) {
      const id = assetArg(ctx, args);
      if (id === null) return;
      const side = obj(args).maxSide;
      await downscaleAsset(ctx, id, typeof side === 'number' && side > 0 ? side : undefined);
    },
  },
  {
    id: 'assets.download',
    title: m.cmdDownload,
    category: 'File',
    icon: 'Download',
    level: 'guided',
    aliases: ['save file', 'download file', 'extract file'],
    enabled: projectOpen,
    async run(ctx, args) {
      const id = assetArg(ctx, args);
      if (id !== null) await downloadAsset(ctx, id);
    },
  },
  {
    id: 'assets.removeUnused',
    title: m.cmdRemoveUnused,
    category: 'File',
    icon: 'Trash2',
    level: 'guided',
    aliases: ['unused files', 'clean up', 'remove unused', '清理'],
    enabled: projectOpen,
    async run(ctx) {
      await removeUnused(ctx);
    },
  },
];

const HELP_TOPICS: readonly HelpTopicDef[] = [
  { id: 'assets', title: m.helpTitle, body: m.helpBody, level: 'guided', showMe: { panel: 'assets' } },
];

export default {
  id: 'assets',
  commands: ASSETS_COMMANDS,
  panels: [
    {
      id: 'assets',
      title: m.panelTitle,
      icon: 'Files',
      slot: 'chart.dock',
      order: 40,
      level: 'guided',
      component: AssetsPanel,
    },
  ],
  helpTopics: HELP_TOPICS,
  init(s) {
    const offSong = followSong(s);
    const offPresence = watchAssetPresence(s);
    return () => {
      offSong();
      offPresence();
    };
  },
} satisfies FeatureModule;
